import { CAPABILITIES, type Capability } from './capabilities';
import type { FeatureId } from './features';

/**
 * Feature flags: whether a piece of the portal is switched on at all.
 *
 * This is the other half of the access question. `capabilities.ts` answers
 * "may this person do X"; a flag answers "is X turned on in this deployment".
 * A user sees something only when both say yes. The two are kept apart on
 * purpose - a flag never names a role, and a role never switches a feature on
 * - so turning Batches off for everybody is one switch here, not an edit to
 * the role table that would then have to be remembered and undone.
 *
 * How a flag takes effect: a switched-off flag removes the capabilities it
 * `gates` from the user's entitlements. Every `can(...)` in the app - the nav
 * filter, the route guards, the buttons inside a screen - already asks for a
 * capability, so all of them honour a flag without knowing flags exist. The
 * few switches with no capability behind them (the assistant pane, a menu
 * item) are read directly with `useFlag`.
 *
 * Where the value comes from, highest first:
 *
 *   1. the user's scope, then up     - Settings > Feature flags: an override at
 *      its tree                        their country, else their region, else
 *                                      Global (and the same levels in the
 *                                      deployment file's `scopes`)
 *   2. the deployment file           - `/feature-flags.json` `flags`, fetched at
 *                                      start up, editable on the server without
 *                                      a rebuild
 *   3. the environment file          - `environment.featureFlags.values`,
 *                                      baked into that environment's bundle
 *   4. the default below
 *
 * Levels 2-4 are the shipped baseline; Settings overrides it per scope. A
 * scope only ever changes whether something is on - who may use it is still
 * the role's capabilities.
 *
 * Like capabilities, none of this is a security boundary. A flag decides what
 * the client draws; the API is what refuses a caller.
 */

/* ---------------------------------------------------------------- the list -- */

/**
 * Every flag. Each product surface in `features.ts` has a flag of the same
 * name, so `assignments` is both the feature and its kill switch; the rest are
 * smaller switches inside a surface, or in the shell.
 *
 * Adding one: add the id here, its definition below, and - if it switches off
 * something that already has a capability - list that capability in `gates`.
 * Nothing else needs to change for the nav and the route guards to follow it.
 */
export const FLAG_IDS = [
  /* product surfaces - one per entry in features.ts */
  'assignments',
  'crm',
  'reportStudio',
  'map',
  'agents',
  'vault',
  'connectors',
  'ratings',
  'faq',

  /* inside a surface */
  'assist',
  'bulkBatches',
  'opportunities',
  'projects',
  'assignmentsNav',
  'pipelineBoard',
  'clientIntelligence',
  'emailIntakeSimulation',
  'opportunitiesBoard',
  'opportunitiesExport',
  'templateBuilder',
  'agentBuilder',
  'vaultCloserJson',
  'vaultFannieMaeXml',

  /* the shell and standalone tools */
  'assistant',
  'promptLibrary'
] as const;

export type FlagId = (typeof FLAG_IDS)[number];

/**
 * Where an effective value was decided: one of the shipped layers, or the id
 * of the scope whose override set it (`global`, `APAC`, `SG`).
 */
export type FlagSource = 'default' | 'environment' | 'deployment' | (string & {});

export type FlagGroup = 'Product surfaces' | 'Assist & automation' | 'Within a surface' | 'Shell & tools';

export const FLAG_GROUPS: readonly FlagGroup[] = ['Product surfaces', 'Assist & automation', 'Within a surface', 'Shell & tools'];

export interface FlagDefinition {
  id: FlagId;
  title: string;
  /** One sentence: what a user stops seeing when this is off. */
  description: string;
  group: FlagGroup;
  /** The surface a smaller flag lives inside, for grouping on the admin page. */
  surface?: FeatureId;
  /** On unless something says otherwise. */
  defaultEnabled: boolean;
  /** Capabilities removed from every user while this flag is off. */
  gates: readonly Capability[];
}

/** Every capability in one namespace - `assignments.*` for the Assignments switch. */
function namespace(prefix: string): Capability[] {
  return CAPABILITIES.filter((capability) => capability.startsWith(`${prefix}.`));
}

export const FLAGS: Record<FlagId, FlagDefinition> = {
  /* -- product surfaces ---------------------------------------------------- */
  assignments: {
    id: 'assignments',
    title: 'Assignments',
    description: 'The assignment record and the work around it: the list, the record, Pipeline, Today, Ask and Batches.',
    group: 'Product surfaces',
    defaultEnabled: true,
    gates: namespace('assignments')
  },
  crm: {
    id: 'crm',
    title: 'CRM',
    description: 'Companies, contacts and people.',
    group: 'Product surfaces',
    defaultEnabled: true,
    gates: namespace('crm')
  },
  reportStudio: {
    id: 'reportStudio',
    title: 'Report Studio',
    description: 'Report Writer and Template Builder.',
    group: 'Product surfaces',
    defaultEnabled: false,
    gates: namespace('reportStudio')
  },
  map: {
    id: 'map',
    title: 'Map & comparables',
    description: 'The map workspace over properties, comparables and assignments, and the property map on every record.',
    group: 'Product surfaces',
    defaultEnabled: true,
    gates: namespace('map')
  },
  agents: {
    id: 'agents',
    title: 'Agents',
    description: 'The agent marketplace and the agent builder.',
    group: 'Product surfaces',
    defaultEnabled: false,
    gates: namespace('agents')
  },
  vault: {
    id: 'vault',
    title: 'Data Vault',
    description: 'Browsing, approving and exporting vault records.',
    group: 'Product surfaces',
    defaultEnabled: false,
    gates: namespace('vault')
  },
  connectors: {
    id: 'connectors',
    title: 'Connectors',
    description: 'The embedded Argus (Power BI) report.',
    group: 'Product surfaces',
    defaultEnabled: false,
    gates: namespace('connectors')
  },
  /*
   * Off by default, as it was when its nav entry was a commented-out block.
   * Switching it on still shows Ratings only to users provisioned with the
   * Ratings role - the flag turns the product on, the role decides who sees it.
   */
  ratings: {
    id: 'ratings',
    title: 'Ratings',
    description: 'The rating and appeal screens, for users provisioned with the Ratings role.',
    group: 'Product surfaces',
    defaultEnabled: false,
    gates: namespace('ratings')
  },
  faq: {
    id: 'faq',
    title: 'FAQ',
    description: 'The help centre.',
    group: 'Product surfaces',
    defaultEnabled: true,
    gates: []
  },

  /* -- inside a surface ---------------------------------------------------- */
  assist: {
    id: 'assist',
    title: 'Assist',
    description: 'Today, Ask, and the drafted figures and suggestions on an assignment.',
    group: 'Assist & automation',
    surface: 'assignments',
    defaultEnabled: false,
    gates: ['assignments.assist']
  },
  bulkBatches: {
    id: 'bulkBatches',
    title: 'Bulk batches',
    description: 'Upload a portfolio and let the batch agent draft it.',
    group: 'Product surfaces',
    surface: 'assignments',
    defaultEnabled: false,
    gates: ['assignments.bulk']
  },
  opportunities: {
    id: 'opportunities',
    title: 'Opportunities',
    description: 'Opportunity intake, from lead to won or lost, and its approval.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: true,
    gates: namespace('opportunities')
  },
  projects: {
    id: 'projects',
    title: 'Projects',
    description: 'The Projects entry under Transactions. Opportunities stay available.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: false,
    gates: []
  },
  assignmentsNav: {
    id: 'assignmentsNav',
    title: 'Assignments menu',
    description: 'The Assignments entry under Transactions. Pipeline and the assignment record stay available.',
    group: 'Within a surface',
    surface: 'assignments',
    defaultEnabled: false,
    gates: []
  },
  pipelineBoard: {
    id: 'pipelineBoard',
    title: 'Pipeline board',
    description: 'The Pipeline board and its menu entry. The assignment record stays available.',
    group: 'Within a surface',
    surface: 'assignments',
    defaultEnabled: false,
    gates: []
  },
  clientIntelligence: {
    id: 'clientIntelligence',
    title: 'Client Intelligence',
    description: 'The Client Intelligence analytics page under CRM, its nav entry and the "Open in Client Intelligence" links.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: false,
    gates: []
  },
  opportunitiesBoard: {
    id: 'opportunitiesBoard',
    title: 'Opportunities board view',
    description: 'The Board view of All opportunities. On, the page still opens on the Table and offers a Table/Board toggle; off, it offers no toggle.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: true,
    gates: []
  },
  opportunitiesExport: {
    id: 'opportunitiesExport',
    title: 'Opportunities export',
    description: 'The Export buttons on All opportunities and Projects, and "Export audit log" on an opportunity.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: false,
    gates: []
  },
  emailIntakeSimulation: {
    id: 'emailIntakeSimulation',
    title: 'Simulate email intake',
    description: 'The "Simulate email intake" button on the bids board, which adds sample bids from a canned mailbox.',
    group: 'Within a surface',
    surface: 'crm',
    defaultEnabled: false,
    gates: []
  },
  templateBuilder: {
    id: 'templateBuilder',
    title: 'Template Builder',
    description: 'Authoring report templates. Report Writer stays available.',
    group: 'Within a surface',
    surface: 'reportStudio',
    defaultEnabled: false,
    gates: ['reportStudio.authorTemplates']
  },
  agentBuilder: {
    id: 'agentBuilder',
    title: 'Build an Agent',
    description: 'Creating and publishing agents. The marketplace stays available.',
    group: 'Within a surface',
    surface: 'agents',
    defaultEnabled: false,
    gates: ['agents.build']
  },
  vaultCloserJson: {
    id: 'vaultCloserJson',
    title: 'Vault export: Closer JSON',
    description: 'The Closer JSON item in the Data Vault export menu. Shown disabled while off.',
    group: 'Within a surface',
    surface: 'vault',
    defaultEnabled: false,
    gates: []
  },
  vaultFannieMaeXml: {
    id: 'vaultFannieMaeXml',
    title: 'Vault export: Fannie Mae XML',
    description: 'The Fannie Mae XML item in the Data Vault export menu. Shown disabled while off.',
    group: 'Within a surface',
    surface: 'vault',
    defaultEnabled: false,
    gates: []
  },

  /* -- the shell and standalone tools -------------------------------------- */
  assistant: {
    id: 'assistant',
    title: 'Universal assistant',
    description: 'The assistant pane on every screen, and its button in the toolbar.',
    group: 'Assist & automation',
    defaultEnabled: false,
    gates: []
  },
  promptLibrary: {
    id: 'promptLibrary',
    title: 'Prompt library',
    description: 'The prompt library page at /prompt, and its nav entry.',
    group: 'Shell & tools',
    defaultEnabled: false,
    gates: []
  }
};

export function isFlagId(value: string): value is FlagId {
  return (FLAG_IDS as readonly string[]).includes(value);
}

/* --------------------------------------------------------------- resolving -- */

/** Flag values as configured - every layer is a partial map. */
export type FlagValues = Partial<Record<FlagId, boolean>>;

export interface FlagLayers {
  /** From `environment.featureFlags.values`. */
  environment?: FlagValues;
  /** From `/feature-flags.json` `flags` on the deployed host. */
  deployment?: FlagValues;
  /**
   * The user's scope chain, Global first, each level's overrides. The deepest
   * level that sets a flag wins.
   */
  scopes?: ReadonlyArray<{ id: string; values?: FlagValues }>;
}

export interface ResolvedFlags {
  enabled: ReadonlySet<FlagId>;
  /** Which layer decided each flag. */
  sources: Readonly<Record<FlagId, FlagSource>>;
}

/** The shipped value of one flag: environment over default, deployment over both. */
export function baselineFlag(layers: Pick<FlagLayers, 'environment' | 'deployment'>, id: FlagId): [boolean, FlagSource] {
  if (typeof layers.deployment?.[id] === 'boolean') {
    return [layers.deployment[id]!, 'deployment'];
  }

  if (typeof layers.environment?.[id] === 'boolean') {
    return [layers.environment[id]!, 'environment'];
  }

  return [FLAGS[id].defaultEnabled, 'default'];
}

/** The deepest layer that sets a flag wins; one that is silent defers upward. */
export function resolveFlags(layers: FlagLayers = {}): ResolvedFlags {
  const enabled = new Set<FlagId>();
  const sources = {} as Record<FlagId, FlagSource>;
  const scopes = [...(layers.scopes ?? [])].reverse();

  for (const id of FLAG_IDS) {
    const scoped = scopes.find((scope) => typeof scope.values?.[id] === 'boolean');
    const [value, source]: [boolean, FlagSource] = scoped
      ? [scoped.values![id]!, scoped.id]
      : baselineFlag(layers, id);

    if (value) {
      enabled.add(id);
    }

    sources[id] = source;
  }

  return { enabled, sources };
}

/** Every flag on - for the callers that reason about roles alone. */
export const ALL_FLAGS_ON: ReadonlySet<FlagId> = new Set(FLAG_IDS);

/** The defaults alone, before any configuration is read. */
export const DEFAULT_FLAGS: ReadonlySet<FlagId> = resolveFlags().enabled;

/** The capabilities a set of enabled flags takes away. */
export function capabilitiesSwitchedOff(enabled: ReadonlySet<FlagId>): Set<Capability> {
  const off = new Set<Capability>();

  for (const id of FLAG_IDS) {
    if (!enabled.has(id)) {
      for (const capability of FLAGS[id].gates) {
        off.add(capability);
      }
    }
  }

  return off;
}

/**
 * Untrusted input - a JSON file, an environment object, local storage - to
 * flag values. Unknown ids and non-boolean values are dropped and reported
 * once, so a typo in the deployment file shows up in the console rather than
 * silently doing nothing.
 */
export function parseFlagValues(input: unknown, origin: string): FlagValues {
  const values: FlagValues = {};

  if (!input || typeof input !== 'object') {
    return values;
  }

  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (!isFlagId(key)) {
      console.warn(`[feature-flags] ${origin}: unknown flag "${key}" ignored.`);
      continue;
    }

    if (typeof value !== 'boolean') {
      console.warn(`[feature-flags] ${origin}: "${key}" must be true or false; ignored.`);
      continue;
    }

    values[key] = value;
  }

  return values;
}
